/**
 * API Keys Service
 *
 * Manages API keys for external services like E2B, Anthropic, etc.
 * Keys are stored securely in the backend and only masked versions are returned to the frontend.
 */

import { isApiAvailable, handleApiError } from './apiUtils';
import { API_BASE as API_BASE_URL, getAuthHeaders } from './apiClient';

/**
 * Available API key types
 */
export const API_KEY_TYPES = {
  E2B: {
    id: 'e2b',
    name: 'E2B Sandbox',
    description: 'Required for sandboxed code execution and repository analysis',
    docUrl: 'https://e2b.dev/docs',
    placeholder: 'e2b_...',
  },
  ANTHROPIC: {
    id: 'anthropic',
    name: 'Anthropic (Claude)',
    description: 'Powers AI insights, career advisor, and LLM chain execution',
    docUrl: 'https://docs.anthropic.com',
    placeholder: 'sk-ant-...',
  },
  OPENAI: {
    id: 'openai',
    name: 'OpenAI',
    description: 'Alternative LLM provider for chain execution',
    docUrl: 'https://platform.openai.com/docs',
    placeholder: 'sk-...',
  },
};

/**
 * Get the status of all API keys (which are configured)
 */
export async function getApiKeyStatus() {
  if (!isApiAvailable()) {
    return {
      keys: Object.keys(API_KEY_TYPES).reduce((acc, key) => {
        acc[API_KEY_TYPES[key].id] = { configured: false, masked: null };
        return acc;
      }, {}),
      error: 'API not available',
    };
  }

  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_BASE_URL}/settings/api-keys/status`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    return handleApiError(error, 'getApiKeyStatus', {
      keys: Object.keys(API_KEY_TYPES).reduce((acc, key) => {
        acc[API_KEY_TYPES[key].id] = { configured: false, masked: null };
        return acc;
      }, {}),
    });
  }
}

/**
 * Set an API key
 * @param {string} keyType - The type of key (e2b, anthropic, openai)
 * @param {string} keyValue - The API key value
 */
export async function setApiKey(keyType, keyValue) {
  if (!isApiAvailable()) {
    return { success: false, error: 'API not available' };
  }

  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_BASE_URL}/settings/api-keys`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        key_type: keyType,
        key_value: keyValue,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    return handleApiError(error, 'setApiKey', { success: false });
  }
}

/**
 * Delete an API key
 * @param {string} keyType - The type of key to delete
 */
export async function deleteApiKey(keyType) {
  if (!isApiAvailable()) {
    return { success: false, error: 'API not available' };
  }

  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_BASE_URL}/settings/api-keys/${keyType}`, {
      method: 'DELETE',
      headers,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    return handleApiError(error, 'deleteApiKey', { success: false });
  }
}

/**
 * Test an API key (verify it works)
 * @param {string} keyType - The type of key to test
 */
export async function testApiKey(keyType) {
  if (!isApiAvailable()) {
    return { success: false, error: 'API not available' };
  }

  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_BASE_URL}/settings/api-keys/${keyType}/test`, {
      method: 'POST',
      headers,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    return handleApiError(error, 'testApiKey', { success: false });
  }
}

export default {
  API_KEY_TYPES,
  getApiKeyStatus,
  setApiKey,
  deleteApiKey,
  testApiKey,
};
